import {useCallback, useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {css} from "@emotion/react";

import {Container} from "@pg-design/grid-module";
import {mb, onDesktop, w100} from "@pg-design/helpers-css";
import {FaqSection, IFaqQuestion} from "@pg-mono/faq";
import {monthWhenVerbose, priceFormat, priceM2Format} from "@pg-mono/string-utils";

import {IRPStore} from "../../../app/rp_reducer";
import {IRegionReportPrice} from "../../../region/types/IRegionReportPrice";
import {FaqCities} from "../utils/generic_faq_cities";
import {IDistrictData} from "./prices_map/PricesMapDistricts";

interface IProps {
    minDistrict: IDistrictData | null;
    maxDistrict: IDistrictData | null;
    region: IRegionReportPrice;
}
const currentYear = new Date().getFullYear();

const getMonthFromDate = (dateString?: string | null) => {
    let date;
    if (!dateString) {
        // If no date is provided, return the previous month based on the current date.
        date = new Date();
        return monthWhenVerbose(date.getMonth());
    }
    date = new Date(dateString);
    return monthWhenVerbose(date.getMonth() + 1);
};

const calculatePercentageDifference = (price1: number, price2: number) => {
    const percentageDifference = ((price1 - price2) / price2) * 100;

    return Math.round(Math.abs(percentageDifference));
};

export const PricesRegionsFAQ = (props: IProps) => {
    const faq = useSelector((store: IRPStore) => store.faq.data);
    const prices = useSelector((state: IRPStore) => state.report.price.prices);
    const latestPriceReport = prices && prices.length > 0 ? prices[prices.length - 1] : null;

    const yearAgoPrices = useSelector((state: IRPStore) => state.report.price.yearAgoPrices);
    const isLower = latestPriceReport && yearAgoPrices.avg_price > latestPriceReport?.avg_price;
    const isLowerPriceM2 = latestPriceReport && yearAgoPrices.avg_price_m2 > latestPriceReport?.avg_price_m2;
    const avgPriceDifference = latestPriceReport && calculatePercentageDifference(yearAgoPrices.avg_price, latestPriceReport?.avg_price);
    const avgPriceM2Difference = latestPriceReport && calculatePercentageDifference(yearAgoPrices.avg_price_m2, latestPriceReport?.avg_price_m2);
    const {minDistrict, maxDistrict, region} = props;
    const [additionalFaqs, setAdditionalFaqs] = useState<IFaqQuestion[] | null>(null);

    const getLocalGenericFaq = useCallback(
        (minDistrict: IDistrictData | null, maxDistrict: IDistrictData | null, region: IRegionReportPrice) => {
            const additionalFaqsArray: IFaqQuestion[] = [];
            const reportMonth = maxDistrict ? getMonthFromDate(maxDistrict.date) : getMonthFromDate(latestPriceReport?.date);

            const {name_declension_where: regionWhere, slug: regionSlug} = region;
            const minDistrictName = minDistrict?.district || "";
            const maxDistrictName = maxDistrict?.district || "";
            const minDistrictPrice = priceFormat(minDistrict?.avg_price_m2 || 0);
            const maxDistrictPrice = priceM2Format(maxDistrict?.avg_price_m2 || 0);
            const latestAvgPriceM2 = priceFormat(latestPriceReport?.avg_price_m2 || 0);
            const latestAvgPrice = priceFormat(latestPriceReport?.avg_price || 0);

            if (FaqCities.includes(regionSlug)) {
                additionalFaqsArray.push(
                    {
                        name: `Która dzielnica ${regionWhere} jest najtańsza?`,
                        answer: `Najtańsza dzielnica ${regionWhere} to ${minDistrictName}. Średnia cena metra kwadratowego kosztuje tu ${minDistrictPrice}.`,
                        answer_text: `Najtańsza dzielnica ${regionWhere} to ${minDistrictName}. Średnia cena metra kwadratowego kosztuje tu ${minDistrictPrice}.`
                    },
                    {
                        name: `Jakie dzielnice są najdroższe ${regionWhere}?`,
                        answer: `Za nowe mieszkanie ${regionWhere} najwięcej zapłacimy w dzielnicy ${maxDistrictName}. Cena sięga tu ${maxDistrictPrice}.`,
                        answer_text: `Za nowe mieszkanie ${regionWhere} najwięcej zapłacimy w dzielnicy ${maxDistrictName}. Cena sięga tu ${maxDistrictPrice}.`
                    }
                );
            }

            const commonFaqsArray: IFaqQuestion[] = [
                {
                    name: `Ile kosztuje metr kwadratowy mieszkania ${regionWhere}?`,
                    answer: `W ${reportMonth} ${currentYear} za metr kwadratowy mieszkania na rynku pierwotnym ${regionWhere} trzeba było zapłacić ${latestAvgPriceM2}. To ${avgPriceM2Difference}% ${isLowerPriceM2 ? "mniej" : "więcej"} niż w ubiegłym roku.`,
                    answer_text: `W ${reportMonth} ${currentYear} za metr kwadratowy mieszkania na rynku pierwotnym ${regionWhere} trzeba było zapłacić ${latestAvgPriceM2}. To ${avgPriceM2Difference}% ${isLowerPriceM2 ? "mniej" : "więcej"} niż w ubiegłym roku.`
                },
                {
                    name: `Ile kosztuje mieszkanie ${regionWhere}?`,
                    answer: `Średnia cena ofertowa mieszkania ${regionWhere} wynosi ${latestAvgPrice}. Co oznacza ${isLower ? "spadek" : "wzrost"} o ${avgPriceDifference}% rok do roku.`,
                    answer_text: `Średnia cena ofertowa mieszkania ${regionWhere} wynosi ${latestAvgPrice}. Co oznacza ${isLower ? "spadek" : "wzrost"} o ${avgPriceDifference}% rok do roku.`
                }
            ];

            setAdditionalFaqs([...additionalFaqsArray, ...commonFaqsArray]);
        },
        [latestPriceReport]
    );

    useEffect(() => {
        getLocalGenericFaq(minDistrict, maxDistrict, region);
    }, [minDistrict, maxDistrict, region]);

    return (
        <>
            {((faq && faq.length > 0) || additionalFaqs) && (
                <Container css={mb(8)}>
                    <FaqSection faqContent={faq} genericQuestions={additionalFaqs} css={faqStyle} />
                </Container>
            )}
        </>
    );
};

const faqStyle = css`
    & h2 {
        text-align: center;
    }

    ${onDesktop(css`
        ${w100};
        max-width: 100%;
    `)};
`;
import {useCallback, useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {css} from "@emotion/react";

import {Container} from "@pg-design/grid-module";
import {mb, onDesktop, w100} from "@pg-design/helpers-css";
import {FaqSection, IFaqQuestion} from "@pg-mono/faq";
import {monthWhenVerbose, priceFormat, priceM2Format} from "@pg-mono/string-utils";

import {IRPStore} from "../../../app/rp_reducer";
import {IRegionReportPrice} from "../../../region/types/IRegionReportPrice";
import {FaqCities} from "../utils/generic_faq_cities";
import {IDistrictData} from "./prices_map/PricesMapDistricts";

interface IProps {
    minDistrict: IDistrictData | null;
    maxDistrict: IDistrictData | null;
    region: IRegionReportPrice;
}
const currentYear = new Date().getFullYear();

const getMonthFromDate = (dateString?: string | null) => {
    let date;
    if (!dateString) {
        // If no date is provided, return the previous month based on the current date.
        date = new Date();
        return monthWhenVerbose(date.getMonth());
    }
    date = new Date(dateString);
    return monthWhenVerbose(date.getMonth() + 1);
};

const calculatePercentageDifference = (price1: number, price2: number) => {
    const percentageDifference = ((price1 - price2) / price2) * 100;

    return Math.round(Math.abs(percentageDifference));
};

export const PricesRegionsFAQ = (props: IProps) => {
    const faq = useSelector((store: IRPStore) => store.faq.data);
    const prices = useSelector((state: IRPStore) => state.report.price.prices);
    const latestPriceReport = prices && prices.length > 0 ? prices[prices.length - 1] : null;

    const yearAgoPrices = useSelector((state: IRPStore) => state.report.price.yearAgoPrices);
    const isLower = latestPriceReport && yearAgoPrices.avg_price > latestPriceReport?.avg_price;
    const isLowerPriceM2 = latestPriceReport && yearAgoPrices.avg_price_m2 > latestPriceReport?.avg_price_m2;
    const avgPriceDifference = latestPriceReport && calculatePercentageDifference(yearAgoPrices.avg_price, latestPriceReport?.avg_price);
    const avgPriceM2Difference = latestPriceReport && calculatePercentageDifference(yearAgoPrices.avg_price_m2, latestPriceReport?.avg_price_m2);
    const {minDistrict, maxDistrict, region} = props;
    const [additionalFaqs, setAdditionalFaqs] = useState<IFaqQuestion[] | null>(null);

    const getLocalGenericFaq = useCallback(
        (minDistrict: IDistrictData | null, maxDistrict: IDistrictData | null, region: IRegionReportPrice) => {
            const additionalFaqsArray: IFaqQuestion[] = [];
            const reportMonth = maxDistrict ? getMonthFromDate(maxDistrict.date) : getMonthFromDate(latestPriceReport?.date);

            const {name_declension_where: regionWhere, slug: regionSlug} = region;
            const minDistrictName = minDistrict?.district || "";
            const maxDistrictName = maxDistrict?.district || "";
            const minDistrictPrice = priceFormat(minDistrict?.avg_price_m2 || 0);
            const maxDistrictPrice = priceM2Format(maxDistrict?.avg_price_m2 || 0);
            const latestAvgPriceM2 = priceFormat(latestPriceReport?.avg_price_m2 || 0);
            const latestAvgPrice = priceFormat(latestPriceReport?.avg_price || 0);

            if (FaqCities.includes(regionSlug)) {
                additionalFaqsArray.push(
                    {
                        name: `Która dzielnica ${regionWhere} jest najtańsza?`,
                        answer: `Najtańsza dzielnica ${regionWhere} to ${minDistrictName}. Średnia cena metra kwadratowego kosztuje tu ${minDistrictPrice}.`,
                        answer_text: `Najtańsza dzielnica ${regionWhere} to ${minDistrictName}. Średnia cena metra kwadratowego kosztuje tu ${minDistrictPrice}.`
                    },
                    {
                        name: `Jakie dzielnice są najdroższe ${regionWhere}?`,
                        answer: `Za nowe mieszkanie ${regionWhere} najwięcej zapłacimy w dzielnicy ${maxDistrictName}. Cena sięga tu ${maxDistrictPrice}.`,
                        answer_text: `Za nowe mieszkanie ${regionWhere} najwięcej zapłacimy w dzielnicy ${maxDistrictName}. Cena sięga tu ${maxDistrictPrice}.`
                    }
                );
            }

            const commonFaqsArray: IFaqQuestion[] = [
                {
                    name: `Ile kosztuje metr kwadratowy mieszkania ${regionWhere}?`,
                    answer: `W ${reportMonth} ${currentYear} za metr kwadratowy mieszkania na rynku pierwotnym ${regionWhere} trzeba było zapłacić ${latestAvgPriceM2}. To ${avgPriceM2Difference}% ${isLowerPriceM2 ? "mniej" : "więcej"} niż w ubiegłym roku.`,
                    answer_text: `W ${reportMonth} ${currentYear} za metr kwadratowy mieszkania na rynku pierwotnym ${regionWhere} trzeba było zapłacić ${latestAvgPriceM2}. To ${avgPriceM2Difference}% ${isLowerPriceM2 ? "mniej" : "więcej"} niż w ubiegłym roku.`
                },
                {
                    name: `Ile kosztuje mieszkanie ${regionWhere}?`,
                    answer: `Średnia cena ofertowa mieszkania ${regionWhere} wynosi ${latestAvgPrice}. Co oznacza ${isLower ? "spadek" : "wzrost"} o ${avgPriceDifference}% rok do roku.`,
                    answer_text: `Średnia cena ofertowa mieszkania ${regionWhere} wynosi ${latestAvgPrice}. Co oznacza ${isLower ? "spadek" : "wzrost"} o ${avgPriceDifference}% rok do roku.`
                }
            ];

            setAdditionalFaqs([...additionalFaqsArray, ...commonFaqsArray]);
        },
        [latestPriceReport]
    );

    useEffect(() => {
        getLocalGenericFaq(minDistrict, maxDistrict, region);
    }, [minDistrict, maxDistrict, region]);

    return (
        <>
            {((faq && faq.length > 0) || additionalFaqs) && (
                <Container css={mb(8)}>
                    <FaqSection faqContent={faq} genericQuestions={additionalFaqs} css={faqStyle} />
                </Container>
            )}
        </>
    );
};

const faqStyle = css`
    & h2 {
        text-align: center;
    }

    ${onDesktop(css`
        ${w100};
        max-width: 100%;
    `)};
`;
